/**
 * Submissions List Component
 * Displays a list of all submitted itineraries sorted by timestamp (oldest / earliest arrival first by default)
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Stack,
  Chip,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  Badge,
  TextField,
  MenuItem,
  Menu,
  DialogContentText,
  FormControl,
  InputLabel,
  Select,
  Tooltip,
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon,
  Refresh as RefreshIcon,
  PictureAsPdf as PdfIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
  AttachFile as AttachFileIcon,
  Download as DownloadIcon,
  Info as InfoIcon,
  SortByAlpha as SortIcon,
  Map as MapIcon,
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import mapboxgl from 'mapbox-gl';
import MapGL, { Marker, NavigationControl } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import * as itineraryApi from '../services/itineraryApi';
import ItineraryMapGL from './ItineraryMapGL';
import { useAuth } from '@signals/app-shell/hooks/useAuth';
import EmptyState from '@signals/app-shell/components/common/EmptyState';
import { useParticipantLabels } from '@signals/app-shell/hooks/useParticipantLabels';
import { useSettingsStore } from '@signals/app-shell/stores';
import {
  formatUtcInstantInTimeZone,
  getPreferredTimeZone,
  parseUtcInstant,
  utcInstantToYmdInTimeZone,
  ymdInTimeZoneToUtcInterval,
} from '@signals/app-shell/utils/time';
import {
  toDateInputValue,
  mergeDateWithExistingTimeIso,
} from '../utils/routeRunDate';

// Extend jsPDF type to include methods added by jspdf-autotable
declare module 'jspdf' {
  interface jsPDF {
    lastAutoTable?: {
      finalY: number;
    };
    previousAutoTable?: {
      finalY: number;
    };
  }
}

interface RouteEntryWithSubmission extends itineraryApi.RouteDataEntry {
  submission: itineraryApi.ItinerarySubmission;
}

type RouteEntryEditForm = Partial<itineraryApi.RouteDataEntry> & {
  route_run_date?: string;
};

const toDateTimeLocalValue = (dateTime?: string): string => {
  if (!dateTime) return format(new Date(), "yyyy-MM-dd'T'HH:mm");
  try {
    return format(parseISO(dateTime), "yyyy-MM-dd'T'HH:mm");
  } catch {
    return dateTime.slice(0, 16);
  }
};

const buildRouteEntryPayload = (
  entry: itineraryApi.RouteDataEntry,
  overrides: Partial<itineraryApi.RouteDataEntry> = {}
): Omit<itineraryApi.RouteDataEntry, 'id' | 'submission_id' | 'created_at'> => ({
  entry_order: entry.entry_order ?? 0,
  route_type: overrides.route_type ?? entry.route_type,
  route_component: overrides.route_component ?? entry.route_component,
  route_component_other: overrides.route_component_other ?? entry.route_component_other,
  latitude: overrides.latitude ?? entry.latitude,
  longitude: overrides.longitude ?? entry.longitude,
  location_name_address: overrides.location_name_address ?? entry.location_name_address,
  arrive_datetime: overrides.arrive_datetime ?? entry.arrive_datetime,
  depart_datetime: overrides.depart_datetime ?? entry.depart_datetime,
  has_documentation: overrides.has_documentation ?? entry.has_documentation ?? false,
  notes: overrides.notes ?? entry.notes,
});

interface GroupedEntries {
  date: string;
  dateKey: string;
  entries: RouteEntryWithSubmission[];
}

interface SubmissionsListProps {
  mapWidth?: number;
  onResizeStart?: (e: React.MouseEvent) => void;
  isDragging?: boolean;
  isActive?: boolean;
  onReopenSubmission?: (submission: itineraryApi.ItinerarySubmission) => void;
}

const SubmissionsList: React.FC<SubmissionsListProps> = ({ mapWidth = 65, onResizeStart, isDragging = false, isActive = true, onReopenSubmission }) => {
  const { user } = useAuth();
  const { singular } = useParticipantLabels();
  const preferredTimezoneSetting = useSettingsStore((state) => state.settings.temporal.preferredTimezone);
  const preferredTimezone = getPreferredTimeZone(preferredTimezoneSetting);
  
  // Check if user is admin or cadre (not a student/trainee)
  const isStudent = user?.roles?.includes('student');
  const usernameLower = user?.username?.toLowerCase() || '';
  const isTrainee = usernameLower.includes('trainee');
  const isStudentInUsername = usernameLower.includes('student');
  const isStudentOrTrainee = isStudent || isTrainee || isStudentInUsername;
  const isAdminOrCadre = !isStudentOrTrainee; // Admin/cadre can search
  
  const [submissions, setSubmissions] = useState<itineraryApi.ItinerarySubmission[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSubmission, setSelectedSubmission] = useState<itineraryApi.ItinerarySubmission | null>(null);
  const [detailsOpen, setDetailsOpen] = useState<boolean>(false);
  const [selectedEntryId, setSelectedEntryId] = useState<number | undefined>(undefined);
  
  // Date range filter - default to today
  const [startDate, setStartDate] = useState<string>(format(new Date(), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState<string>(format(new Date(), 'yyyy-MM-dd'));

  const parseYmdLocal = (ymd: string): Date => {
    const [y, m, d] = ymd.split('-').map(Number);
    return new Date(y, (m || 1) - 1, d || 1);
  };

  const applyQuickRange = (days: number | null) => {
    if (days === null) {
      setStartDate('');
      setEndDate('');
      return;
    }
    const today = new Date();
    const start = new Date(today);
    // Inclusive range: "7 days" = last 6 days + today.
    start.setDate(today.getDate() - Math.max(0, days - 1));
    setStartDate(format(start, 'yyyy-MM-dd'));
    setEndDate(format(today, 'yyyy-MM-dd'));
  };

  const activeQuickRange = useMemo<'today' | '7d' | '30d' | 'all' | null>(() => {
    if (!startDate && !endDate) return 'all';
    if (!startDate || !endDate) return null;

    const today = format(new Date(), 'yyyy-MM-dd');
    if (endDate !== today) return null;

    const start = parseYmdLocal(startDate);
    const end = parseYmdLocal(endDate);
    const dayMs = 24 * 60 * 60 * 1000;
    const diffDays = Math.round((end.getTime() - start.getTime()) / dayMs) + 1;

    if (diffDays === 1) return 'today';
    if (diffDays === 7) return '7d';
    if (diffDays === 30) return '30d';
    return null;
  }, [startDate, endDate]);
  
  // Student filter - for admin/cadre search
  const [studentFilter, setStudentFilter] = useState<string>('');
  
  // Sort order - oldest first by default (toggle for newest first)
  const [sortNewestFirst, setSortNewestFirst] = useState<boolean>(false);

  const formatDateKey = useCallback((ymd: string): string => {
    const [year, month, day] = ymd.split('-').map(Number);
    if (!year || !month || !day) return ymd;

    return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString(undefined, {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      timeZone: 'UTC',
    });
  }, []);

  const getInstantTime = useCallback((value?: string): number => {
    return parseUtcInstant(value)?.getTime() ?? 0;
  }, []);
  
  // Submission filter - for clicking on map markers
  const [selectedSubmissionId, setSelectedSubmissionId] = useState<number | null>(null);
  
  // Map ref to control zoom
  const mapRef = useRef<any>(null);
  
  // Flash trigger for map markers
  const [flashTrigger, setFlashTrigger] = useState<number | null>(null);
  
  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [submissionToDelete, setSubmissionToDelete] = useState<itineraryApi.ItinerarySubmission | null>(null);
  const [routeEntryToDelete, setRouteEntryToDelete] = useState<RouteEntryWithSubmission | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteType, setDeleteType] = useState<'entry' | 'submission'>('entry');
  
  // Menu state for actions
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedMenuEntry, setSelectedMenuEntry] = useState<RouteEntryWithSubmission | null>(null);
  
  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<RouteEntryWithSubmission | null>(null);
  const [editFormData, setEditFormData] = useState<RouteEntryEditForm>({});
  const [saving, setSaving] = useState(false);
  
  // Add Entry dialog state
  const [addEntryDialogOpen, setAddEntryDialogOpen] = useState(false);
  const [addingToSubmission, setAddingToSubmission] = useState<itineraryApi.ItinerarySubmission | null>(null);
  const [addEntryFormData, setAddEntryFormData] = useState<Partial<itineraryApi.RouteDataEntry>>({
    route_type: 'Pre-route',
    route_component: 'CS',
    latitude: 0,
    longitude: 0,
    location_name_address: '',
    arrive_datetime: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
    depart_datetime: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
    has_documentation: false,
    notes: '',
  });
  
  // Map picker state for Add/Edit dialogs
  const [showAddMapPicker, setShowAddMapPicker] = useState(false);
  const [showEditMapPicker, setShowEditMapPicker] = useState(false);
  const [isReverseGeocoding, setIsReverseGeocoding] = useState(false);
  const [mapStyle, setMapStyle] = useState<string>('streets');
  
  // Map style options
  const mapStyles: { [key: string]: string } = {
    streets: 'mapbox://styles/mapbox/streets-v12',
    satellite: 'mapbox://styles/mapbox/satellite-streets-v12',
    dark: 'mapbox://styles/mapbox/dark-v11',
    light: 'mapbox://styles/mapbox/light-v11',
    outdoors: 'mapbox://styles/mapbox/outdoors-v12',
  };

  const normalizeStatus = (status?: string) => (status || '').trim().toLowerCase();

  const isDraftSubmission = (submission?: itineraryApi.ItinerarySubmission | null) => {
    return normalizeStatus(submission?.status) === 'draft';
  };

  const getStatusLabel = (status?: string) => {
    const normalizedStatus = normalizeStatus(status);
    if (normalizedStatus === 'draft') return 'Draft';
    if (normalizedStatus === 'complete' || normalizedStatus === 'submitted') return 'Submitted';
    if (normalizedStatus === 'reviewed') return 'Reviewed';
    if (normalizedStatus === 'approved') return 'Approved';
    return 'Submitted';
  };

  const getStatusColor = (status?: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    const normalizedStatus = normalizeStatus(status);
    if (normalizedStatus === 'draft') return 'warning';
    if (normalizedStatus === 'complete' || normalizedStatus === 'submitted') return 'success';
    if (normalizedStatus === 'reviewed') return 'info';
    if (normalizedStatus === 'approved') return 'primary';
    return 'success';
  };

  const groupedEntries = useMemo<GroupedEntries[]>(() => {
    const allEntries: RouteEntryWithSubmission[] = [];
    submissions.forEach((submission) => {
      if (submission.route_entries) {
        submission.route_entries.forEach((entry) => {
          allEntries.push({
            ...entry,
            submission,
          });
        });
      }
    });

    const grouped: Record<string, RouteEntryWithSubmission[]> = {};
    allEntries.forEach((entry) => {
      const dateKey = utcInstantToYmdInTimeZone(entry.submission.route_run_datetime, preferredTimezone);
      if (!dateKey) return;
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(entry);
    });

    return Object.entries(grouped)
      .map(([dateKey, entries]) => ({
        date: formatDateKey(dateKey),
        dateKey,
        entries: entries.sort((a, b) => {
          const timeA = getInstantTime(a.arrive_datetime);
          const timeB = getInstantTime(b.arrive_datetime);
          return sortNewestFirst ? timeB - timeA : timeA - timeB;
        }),
      }))
      .sort((a, b) => sortNewestFirst
        ? b.dateKey.localeCompare(a.dateKey)
        : a.dateKey.localeCompare(b.dateKey));
  }, [formatDateKey, getInstantTime, preferredTimezone, sortNewestFirst, submissions]);
  
  // Load submissions for the selected date range from the API (not only the
  // newest 100 globally). Client-side filtering alone cannot see historic rows
  // once newer submissions fill the first page.
  const loadSubmissions = useCallback(async () => {
    console.log('[SubmissionsList] Loading submissions...');
    setLoading(true);
    setError(null);
    
    try {
      const params: Parameters<typeof itineraryApi.listItinerarySubmissions>[0] = {
        page: 1,
        page_size: 100,
      };
      if (startDate) {
        const interval = ymdInTimeZoneToUtcInterval(startDate, preferredTimezone);
        params.start_date = interval?.startUtc || `${startDate}T00:00:00.000Z`;
      }
      if (endDate) {
        const interval = ymdInTimeZoneToUtcInterval(endDate, preferredTimezone);
        params.end_date = interval?.endUtc || `${endDate}T23:59:59.999Z`;
      }

      const firstPage = await itineraryApi.listItinerarySubmissions(params);
      const items = [...(firstPage.items || [])];
      const totalPages = Math.max(1, firstPage.total_pages || 1);
      for (let page = 2; page <= totalPages; page++) {
        const next = await itineraryApi.listItinerarySubmissions({ ...params, page });
        items.push(...(next.items || []));
      }

      console.log('[SubmissionsList] API response:', items.length, 'of', firstPage.total ?? items.length, 'items');
      setSubmissions(items);
    } catch (err: any) {
      console.error('[SubmissionsList] Error loading submissions:', err);
      setError('Failed to load submissions. Please try again.');
    } finally {
      console.log('[SubmissionsList] Loading complete');
      setLoading(false);
    }
  }, [endDate, preferredTimezone, startDate]);
  
  // Filter entries by date range, student, and submission
  const filteredGroupedEntries = groupedEntries.filter(group => {
    if (!startDate && !endDate) return true;
    const groupDate = group.dateKey;
    
    // Check if groupDate is within the range
    const isAfterStart = !startDate || groupDate >= startDate;
    const isBeforeEnd = !endDate || groupDate <= endDate;
    
    return isAfterStart && isBeforeEnd;
  }).map(group => {
    let filteredEntries = group.entries;
    
    // Filter by student number if search is active
    if (studentFilter && isAdminOrCadre) {
      filteredEntries = filteredEntries.filter(entry => 
        entry.submission.student_number?.toLowerCase().includes(studentFilter.toLowerCase())
      );
    }
    
    // If a submission is selected, filter entries for that submission only
    if (selectedSubmissionId !== null) {
      filteredEntries = filteredEntries.filter(entry => entry.submission.id === selectedSubmissionId);
    }
    
    // Sort entries within each group
    filteredEntries = [...filteredEntries].sort((a, b) => {
      const timeA = getInstantTime(a.arrive_datetime);
      const timeB = getInstantTime(b.arrive_datetime);
      return sortNewestFirst ? timeB - timeA : timeA - timeB;
    });
    
    return {
      ...group,
      entries: filteredEntries
    };
  }).filter(group => group.entries.length > 0) // Remove empty groups
  .sort((a, b) => {
    // Sort groups by date
    return sortNewestFirst
      ? b.dateKey.localeCompare(a.dateKey)
      : a.dateKey.localeCompare(b.dateKey);
  });

  // Get all entries for the map (filtered by date range and submission)
  const allEntries = filteredGroupedEntries.flatMap(group => group.entries);
  const filteredNoEntrySubmissions = submissions.filter((submission) => {
    if (submission.route_entries && submission.route_entries.length > 0) return false;
    const routeDate = utcInstantToYmdInTimeZone(submission.route_run_datetime, preferredTimezone);
    const isAfterStart = !startDate || routeDate >= startDate;
    const isBeforeEnd = !endDate || routeDate <= endDate;
    const matchesStudent = !studentFilter || !isAdminOrCadre || submission.student_number?.toLowerCase().includes(studentFilter.toLowerCase());
    const matchesSubmission = selectedSubmissionId === null || submission.id === selectedSubmissionId;
    return isAfterStart && isBeforeEnd && matchesStudent && matchesSubmission;
  });
  
  // Reload when the tab becomes active so newly saved drafts appear immediately.
  useEffect(() => {
    if (isActive) {
      loadSubmissions();
    }
  }, [isActive, loadSubmissions]);
  
  // Close details dialog
  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedSubmission(null);
  };
  
  // Open details dialog
  const handleViewDetails = (submission: itineraryApi.ItinerarySubmission) => {
    setSelectedSubmission(submission);
    setDetailsOpen(true);
    handleMenuClose();
  };

  const handleReopenSubmission = (submission: itineraryApi.ItinerarySubmission) => {
    onReopenSubmission?.(submission);
    setDetailsOpen(false);
    setSelectedSubmission(null);
    handleMenuClose();
  };
  
  // Format date/time
  const formatDateTime = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    return formatUtcInstantInTimeZone(dateString, preferredTimezone) || dateString;
  };
  
  // Export all filtered entries to PDF
  const handleExportAllToPDF = () => {
    if (filteredGroupedEntries.length === 0) return;
    
    // Export all filtered groups
    filteredGroupedEntries.forEach((group, index) => {
      // For multiple days, we could combine into one PDF or separate
      // For now, let's create one combined PDF
      if (index === 0) {
        handleExportDayToPDF(group, true); // First group starts the PDF
      }
    });
  };

  // Export day's route entries to PDF
  const handleExportDayToPDF = (group: GroupedEntries, _isPartOfBatch = false) => {
    const doc = new jsPDF();
    let yPosition = 20;
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 14;
    const contentWidth = pageWidth - (margin * 2);
    
    // Add title
    doc.setFontSize(18);
    doc.setTextColor(46, 134, 171); // Primary color
    doc.text('Coordination Itinerary Report', margin, yPosition);
    yPosition += 10;
    
    // Add route date
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text(`Route Date: ${group.date}`, margin, yPosition);
    yPosition += 8;
    
    // Add metadata
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Total Entries: ${group.entries.length}`, margin, yPosition);
    yPosition += 5;
    doc.text(`Generated: ${format(new Date(), 'MMM dd, yyyy h:mm a')}`, margin, yPosition);
    yPosition += 10;
    
    // Group entries by submission/student
    const submissionGroups = group.entries.reduce((acc, entry) => {
      const subId = entry.submission.id;
      if (!acc[subId!]) {
        acc[subId!] = {
          submission: entry.submission,
          entries: []
        };
      }
      acc[subId!].entries.push(entry);
      return acc;
    }, {} as Record<number, { submission: itineraryApi.ItinerarySubmission; entries: RouteEntryWithSubmission[] }>);
    
    // For each submission, add header and routes (bio info only once at top of each submission section)
    Object.values(submissionGroups).forEach((subGroup) => {
      const submission = subGroup.submission;
      const entries = subGroup.entries;
      
      // Check if we need a new page
      if (yPosition > 240) {
        doc.addPage();
        yPosition = 20;
      }
      
      // Student/Submission Header with Bio Info
      doc.setFillColor(46, 134, 171);
      doc.rect(margin, yPosition, contentWidth, 8, 'F');
      doc.setFontSize(12);
      doc.setTextColor(255, 255, 255);
      doc.text(`${submission.student_number} - ${entries.length} ${entries.length === 1 ? 'Location' : 'Locations'}`, margin + 2, yPosition + 5.5);
      yPosition += 12;
      
      // Bio and Vehicle info on same line (compact format) - only show once per submission
      doc.setFontSize(9);
      doc.setTextColor(60, 60, 60);
      doc.setFont('helvetica', 'bold');
      
      const bioItems: string[] = [];
      if (submission.gender) bioItems.push(`Gender: ${submission.gender}`);
      if (submission.height) bioItems.push(`Height: ${submission.height}`);
      if (submission.weight) bioItems.push(`Weight: ${submission.weight}`);
      if (submission.hair_typecolor) bioItems.push(`Hair: ${submission.hair_typecolor}`);
      if (submission.facial_hair_typecolor) bioItems.push(`Facial Hair: ${submission.facial_hair_typecolor}`);
      
      const vehicleItems: string[] = [];
      if (submission.veh_make || submission.veh_model) {
        vehicleItems.push(`Vehicle: ${[submission.veh_make, submission.veh_model].filter(Boolean).join(' ')}`);
      }
      if (submission.veh_color) vehicleItems.push(`Color: ${submission.veh_color}`);
      if (submission.veh_plate) vehicleItems.push(`Plate: ${submission.veh_plate}`);
      
      // Print bio info compactly
      if (bioItems.length > 0 || vehicleItems.length > 0) {
        doc.text('Bio: ', margin, yPosition);
        doc.setFont('helvetica', 'normal');
        const bioText = bioItems.length > 0 ? bioItems.join(' | ') : 'N/A';
        doc.text(bioText, margin + 10, yPosition);
        yPosition += 5;
        
        doc.setFont('helvetica', 'bold');
        doc.text('Vehicle: ', margin, yPosition);
        doc.setFont('helvetica', 'normal');
        const vehicleText = vehicleItems.length > 0 ? vehicleItems.join(' | ') : 'N/A';
        doc.text(vehicleText, margin + 15, yPosition);
        yPosition += 8;
      }
      
      // Sort entries by arrival time (earliest first) for chronological order
      const sortedEntries = [...entries].sort((a, b) => {
        const timeA = getInstantTime(a.arrive_datetime);
        const timeB = getInstantTime(b.arrive_datetime);
        return timeA - timeB; // Ascending order (earliest first)
      });
      
      // Prepare table data with entry notes
      const tableData = sortedEntries.map((entry, index) => {
        const rows: any[] = [];
        
        // Main entry row
        const locationLines = [
          entry.location_name_address || 'No address',
          `Coordinates: ${entry.latitude.toFixed(6)}, ${entry.longitude.toFixed(6)}`,
        ];
        rows.push([
          (index + 1).toString(),
          `${entry.route_type}\n${entry.route_component}${entry.route_component_other ? `\n(${entry.route_component_other})` : ''}`,
          locationLines.join('\n'),
          formatDateTime(entry.arrive_datetime).replace(' ', '\n'),
          formatDateTime(entry.depart_datetime).replace(' ', '\n'),
        ]);
        
        // Add entry notes as a separate row if present
        if (entry.notes) {
          rows.push([
            '',
            { content: `Notes: ${entry.notes}`, colSpan: 4, styles: { fontStyle: 'italic', textColor: [100, 100, 100] } }
          ]);
        }
        
        return rows;
      }).flat();
      
      // Add table - full width
      autoTable(doc, {
        startY: yPosition,
        head: [['#', 'Route Type', 'Location', 'Arrival', 'Departure']],
        body: tableData,
        styles: {
          fontSize: 8,
          cellPadding: 2,
        },
        headStyles: {
          fillColor: [46, 134, 171],
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          fontSize: 9,
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245],
        },
        columnStyles: {
          0: { cellWidth: 12, halign: 'center' },
          1: { cellWidth: 30 },
          2: { cellWidth: 'auto' }, // Location takes remaining space
          3: { cellWidth: 32 },
          4: { cellWidth: 32 },
        },
        margin: { left: margin, right: margin },
        tableWidth: 'auto',
        didDrawPage: (data) => {
          yPosition = data.cursor?.y || yPosition;
        }
      });
      
      // Update yPosition after table
      yPosition = (doc as any).lastAutoTable.finalY + 8;
      
      // General Notes Section
      if (submission.notes) {
        if (yPosition > 250) {
          doc.addPage();
          yPosition = 20;
        }
        
        doc.setTextColor(60, 60, 60);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.text('Notes:', margin, yPosition);
        doc.setFont('helvetica', 'normal');
        const noteLines = doc.splitTextToSize(submission.notes, contentWidth - 15);
        doc.text(noteLines, margin + 15, yPosition);
        yPosition += (noteLines.length * 4) + 6;
      }
      
      // Add spacing between submissions
      yPosition += 5;
    });
    
    // Add footer to all pages (page number only, no CUI)
    const pageCount = (doc as any).internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      
      // Page number
      doc.text(
        `Page ${i} of ${pageCount}`,
        doc.internal.pageSize.getWidth() / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: 'center' }
      );
    }
    
    // Save PDF
    const fileName = `Itinerary_${group.date.replace(/,/g, '').replace(/ /g, '_')}.pdf`;
    doc.save(fileName);
  };
  
  if (loading) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading submissions...
        </Typography>
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
        <Button
          startIcon={<RefreshIcon />}
          onClick={loadSubmissions}
          sx={{ mt: 2 }}
        >
          Try Again
        </Button>
      </Box>
    );
  }
  
  // Handle entry click from list
  const handleEntryClickFromList = (entry: RouteEntryWithSubmission) => {
    // If clicking the same entry, deselect it and return to full extent
    if (selectedEntryId === entry.id) {
      setSelectedEntryId(undefined);
      setSelectedSubmissionId(null);
      
      // Zoom to show only the entries currently displayed on the map (respects active filters)
      if (mapRef.current && allEntries.length > 0) {
        const coordinates: [number, number][] = [];
        allEntries.forEach(e => {
          if (e.latitude && e.longitude) {
            coordinates.push([e.longitude, e.latitude]);
          }
        });
        
        if (coordinates.length > 0) {
          const bounds = coordinates.reduce((bounds, coord) => {
            return bounds.extend(coord);
          }, new mapboxgl.LngLatBounds(coordinates[0], coordinates[0]));
          
          mapRef.current.fitBounds(bounds, {
            padding: 80,
            duration: 1500,
            maxZoom: 14
          });
        }
      }
      return;
    }
    
    // Select new entry
    setSelectedEntryId(entry.id);
    setSelectedSubmissionId(null); // Clear submission filter when clicking individual entry
    
    // Trigger flash animation on the map marker
    const entryIndex = allEntries.findIndex((e: RouteEntryWithSubmission) => e.id === entry.id);
    if (entryIndex >= 0) {
      setFlashTrigger(prev => prev === entryIndex ? entryIndex + 0.001 : entryIndex); // Slight change to re-trigger
    }
    
    // Zoom to the entry location on the map
    if (mapRef.current && entry.latitude && entry.longitude) {
      mapRef.current.flyTo({
        center: [entry.longitude, entry.latitude],
        zoom: 15,
        duration: 1500,
        essential: true
      });
    }
    
    // Scroll to top to see map on mobile
    if (window.innerWidth < 960) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Handle entry click from map
  const handleEntryClickFromMap = (entry: RouteEntryWithSubmission) => {
    // If clicking the same entry, deselect it and return to full extent
    if (selectedEntryId === entry.id) {
      setSelectedEntryId(undefined);
      setSelectedSubmissionId(null);
      
      // Zoom to show only the entries currently displayed on the map (respects active filters)
      if (mapRef.current && allEntries.length > 0) {
        const coordinates: [number, number][] = [];
        allEntries.forEach(e => {
          if (e.latitude && e.longitude) {
            coordinates.push([e.longitude, e.latitude]);
          }
        });
        
        if (coordinates.length > 0) {
          const bounds = coordinates.reduce((bounds, coord) => {
            return bounds.extend(coord);
          }, new mapboxgl.LngLatBounds(coordinates[0], coordinates[0]));
          
          mapRef.current.fitBounds(bounds, {
            padding: 80,
            duration: 1500,
            maxZoom: 14
          });
        }
      }
      return;
    }
    
    // Select new entry
    setSelectedEntryId(entry.id);
    
    // Filter to show only entries from this submission
    setSelectedSubmissionId(entry.submission.id || null);
    
    // Scroll to the entry in the list
    setTimeout(() => {
      const element = document.getElementById(`entry-${entry.id}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };
  
  // Handle menu open
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, entry: RouteEntryWithSubmission) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedMenuEntry(entry);
  };
  
  // Handle menu close
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedMenuEntry(null);
  };
  
  // Handle edit button click
  const handleEditClick = () => {
    if (selectedMenuEntry) {
      setEditingEntry(selectedMenuEntry);
      setEditFormData({
        route_type: selectedMenuEntry.route_type,
        route_component: selectedMenuEntry.route_component,
        route_component_other: selectedMenuEntry.route_component_other,
        location_name_address: selectedMenuEntry.location_name_address,
        latitude: selectedMenuEntry.latitude,
        longitude: selectedMenuEntry.longitude,
        arrive_datetime: selectedMenuEntry.arrive_datetime,
        depart_datetime: selectedMenuEntry.depart_datetime,
        notes: selectedMenuEntry.notes || '',
        route_run_date: toDateInputValue(selectedMenuEntry.submission.route_run_datetime),
      });
      setEditDialogOpen(true);
      handleMenuClose();
    }
  };

  const handleEditRouteRunChange = (value: string) => {
    setEditFormData((prev) => ({
      ...prev,
      route_run_date: value,
    }));
  };
  
  // Handle save edited entry
  const handleSaveEdit = async () => {
    if (!editingEntry || !editingEntry.id) return;
    
    setSaving(true);
    setError(null);
    
    try {
      const originalRouteRunDate = toDateInputValue(editingEntry.submission.route_run_datetime);
      const routeRunDateChanged =
        !!editFormData.route_run_date &&
        editFormData.route_run_date !== originalRouteRunDate;

      const entryUpdates: Partial<itineraryApi.RouteDataEntry> = {
        route_type: editFormData.route_type,
        route_component: editFormData.route_component,
        route_component_other: editFormData.route_component_other,
        location_name_address: editFormData.location_name_address,
        latitude: editFormData.latitude,
        longitude: editFormData.longitude,
        notes: editFormData.notes,
      };

      if (routeRunDateChanged && editingEntry.submission.id) {
        const newRouteRunIso = mergeDateWithExistingTimeIso(
          editFormData.route_run_date!,
          editingEntry.submission.route_run_datetime
        );
        const currentEntryUpdates = {
          ...entryUpdates,
          arrive_datetime: mergeDateWithExistingTimeIso(
            editFormData.route_run_date!,
            editFormData.arrive_datetime || editingEntry.arrive_datetime
          ),
          depart_datetime: mergeDateWithExistingTimeIso(
            editFormData.route_run_date!,
            editFormData.depart_datetime || editingEntry.depart_datetime
          ),
        };
        const routeEntries = (editingEntry.submission.route_entries || []).map((entry) => {
          if (entry.id === editingEntry.id) {
            return buildRouteEntryPayload(entry, currentEntryUpdates);
          }
          return buildRouteEntryPayload(entry, {
            arrive_datetime: mergeDateWithExistingTimeIso(editFormData.route_run_date!, entry.arrive_datetime),
            depart_datetime: mergeDateWithExistingTimeIso(editFormData.route_run_date!, entry.depart_datetime),
          });
        });

        await itineraryApi.updateItinerarySubmission(editingEntry.submission.id, {
          route_run_datetime: newRouteRunIso,
          route_entries: routeEntries,
        });
      } else {
        await itineraryApi.updateRouteEntry(editingEntry.id, {
          ...entryUpdates,
          arrive_datetime: editFormData.arrive_datetime,
          depart_datetime: editFormData.depart_datetime,
        });
      }

      setEditDialogOpen(false);
      setEditingEntry(null);
      setEditFormData({});
      await loadSubmissions(); // Reload to show updated data
    } catch (err: any) {
      console.error('Error updating entry:', err);
      setError(err.response?.data?.detail || 'Failed to update entry');
    } finally {
      setSaving(false);
    }
  };
  
  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditDialogOpen(false);
    setEditingEntry(null);
    setEditFormData({});
    setShowEditMapPicker(false);
  };
  
  // Handle add entry to submission
  const handleAddEntryClick = () => {
    if (selectedMenuEntry?.submission) {
      const submission = selectedMenuEntry.submission;
      // Pre-populate with submission's route run datetime
      const routeRunDateTime = submission.route_run_datetime 
        ? format(parseISO(submission.route_run_datetime), "yyyy-MM-dd'T'HH:mm")
        : format(new Date(), "yyyy-MM-dd'T'HH:mm");
      
      setAddingToSubmission(submission);
      setAddEntryFormData({
        route_type: 'Pre-route',
        route_component: 'CS',
        latitude: 0,
        longitude: 0,
        location_name_address: '',
        arrive_datetime: routeRunDateTime,
        depart_datetime: routeRunDateTime,
        has_documentation: false,
        notes: '',
      });
      setAddEntryDialogOpen(true);
      handleMenuClose();
    }
  };
  
  // Handle save new entry
  const handleSaveNewEntry = async () => {
    if (!addingToSubmission?.id) return;
    
    // Validation
    if (!addEntryFormData.location_name_address) {
      setError('Please enter a location name/address');
      return;
    }
    
    setSaving(true);
    setError(null);
    
    try {
      const entryData = {
        entry_order: 0, // Will be set by backend
        route_type: addEntryFormData.route_type || 'Pre-route',
        route_component: addEntryFormData.route_component || 'CS',
        route_component_other: addEntryFormData.route_component_other,
        latitude: addEntryFormData.latitude || 0,
        longitude: addEntryFormData.longitude || 0,
        location_name_address: addEntryFormData.location_name_address || '',
        arrive_datetime: new Date(addEntryFormData.arrive_datetime || '').toISOString(),
        depart_datetime: new Date(addEntryFormData.depart_datetime || '').toISOString(),
        has_documentation: addEntryFormData.has_documentation || false,
        notes: addEntryFormData.notes,
      };
      
      await itineraryApi.addRouteEntry(addingToSubmission.id, entryData);
      setAddEntryDialogOpen(false);
      setAddingToSubmission(null);
      setAddEntryFormData({});
      await loadSubmissions(); // Reload to show new entry
    } catch (err: any) {
      console.error('Error adding entry:', err);
      setError(err.response?.data?.detail || 'Failed to add entry');
    } finally {
      setSaving(false);
    }
  };
  
  // Handle cancel add entry
  const handleCancelAddEntry = () => {
    setAddEntryDialogOpen(false);
    setAddingToSubmission(null);
    setAddEntryFormData({});
    setShowAddMapPicker(false);
  };
  
  // Reverse geocode coordinates to address
  const reverseGeocode = async (lat: number, lon: number): Promise<string | null> => {
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${lon},${lat}.json?access_token=${process.env.REACT_APP_MAPBOX_TOKEN}`
      );
      const data = await response.json();
      if (data.features && data.features.length > 0) {
        return data.features[0].place_name;
      }
      return null;
    } catch (error) {
      console.error('Reverse geocoding failed:', error);
      return null;
    }
  };
  
  // Handle map click for Add Entry dialog
  const handleAddMapClick = async (event: mapboxgl.MapLayerMouseEvent) => {
    const { lng, lat } = event.lngLat;
    setAddEntryFormData(prev => ({
      ...prev,
      latitude: lat,
      longitude: lng,
    }));
    
    // Reverse geocode to get address
    setIsReverseGeocoding(true);
    const address = await reverseGeocode(lat, lng);
    if (address) {
      setAddEntryFormData(prev => ({
        ...prev,
        location_name_address: address,
      }));
    }
    setIsReverseGeocoding(false);
    setShowAddMapPicker(false);
  };
  
  // Handle map click for Edit Entry dialog
  const handleEditMapClick = async (event: mapboxgl.MapLayerMouseEvent) => {
    const { lng, lat } = event.lngLat;
    setEditFormData(prev => ({
      ...prev,
      latitude: lat,
      longitude: lng,
    }));
    
    // Reverse geocode to get address
    setIsReverseGeocoding(true);
    const address = await reverseGeocode(lat, lng);
    if (address) {
      setEditFormData(prev => ({
        ...prev,
        location_name_address: address,
      }));
    }
    setIsReverseGeocoding(false);
    setShowEditMapPicker(false);
  };
  
  // Handle delete entry click
  const handleDeleteEntryClick = () => {
    if (selectedMenuEntry) {
      setRouteEntryToDelete(selectedMenuEntry);
      setDeleteType('entry');
      setDeleteDialogOpen(true);
      handleMenuClose();
    }
  };
  
  // Handle delete submission click
  const handleDeleteSubmissionClick = () => {
    if (selectedMenuEntry) {
      setSubmissionToDelete(selectedMenuEntry.submission);
      setDeleteType('submission');
      setDeleteDialogOpen(true);
      handleMenuClose();
    }
  };
  
  // Handle delete confirmation
  const handleConfirmDelete = async () => {
    setDeleting(true);
    setError(null);
    
    try {
      if (deleteType === 'entry' && routeEntryToDelete?.id) {
        await itineraryApi.deleteRouteEntry(routeEntryToDelete.id);
      } else if (deleteType === 'submission' && submissionToDelete?.id) {
        await itineraryApi.deleteItinerarySubmission(submissionToDelete.id);
      }
      
      setDeleteDialogOpen(false);
      setSubmissionToDelete(null);
      setRouteEntryToDelete(null);
      await loadSubmissions(); // Reload list
    } catch (err: any) {
      console.error('Error deleting:', err);
      setError(err.response?.data?.detail || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };


  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, height: '100%', overflow: 'hidden' }}>
      {/* Map Panel - always rendered so the layout stays consistent even when filters return no entries */}
      <Box sx={{
        width: { xs: '100%', md: `${mapWidth}%` },
        height: { xs: '400px', md: '100%' },
        position: 'relative',
        flexShrink: 0,
      }}>
        <ItineraryMapGL
          routeEntries={allEntries}
          viewMode="view"
          showLegend={true}
          selectedEntryIndex={allEntries.findIndex((e: RouteEntryWithSubmission) => e.id === selectedEntryId)}
          onEntryClick={handleEntryClickFromMap}
          onMapReady={(map) => { mapRef.current = map; }}
          flashEntryIndex={flashTrigger !== null ? Math.floor(flashTrigger) : null}
        />
      </Box>

      {/* Resizable Divider - Desktop only */}
      {onResizeStart && (
        <Box
          onMouseDown={onResizeStart}
          sx={{
            display: { xs: 'none', md: 'block' },
            width: '4px',
            bgcolor: isDragging ? 'primary.main' : 'divider',
            cursor: 'col-resize',
            flexShrink: 0,
            '&:hover': {
              bgcolor: 'primary.light',
            },
            transition: 'background-color 0.2s',
          }}
        />
      )}

      {/* Content Panel */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
      {/* Header with Date Selector and Map Style */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <AssignmentIcon color="primary" sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h6" fontWeight={600}>
              Submitted Itineraries
              {selectedSubmissionId && (
                <Chip 
                  label="Filtered" 
                  size="small" 
                  color="warning"
                  sx={{ ml: 1 }}
                  onDelete={() => setSelectedSubmissionId(null)}
                />
              )}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {allEntries.length} route {allEntries.length === 1 ? 'entry' : 'entries'} 
              {selectedSubmissionId ? ' (filtered by submission)' : ` from ${submissions.length} ${submissions.length === 1 ? 'submission' : 'submissions'}`}
            </Typography>
          </Box>
        </Box>
        {/* Action Buttons */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Tooltip title="Export PDF">
            <IconButton 
              onClick={handleExportAllToPDF}
              size="small"
              disabled={filteredGroupedEntries.length === 0}
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                '&:hover': { bgcolor: 'primary.dark' },
                '&.Mui-disabled': { bgcolor: 'action.disabledBackground', color: 'action.disabled' },
              }}
            >
              <PdfIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={sortNewestFirst ? "Newest First" : "Oldest First"}>
            <IconButton 
              onClick={() => setSortNewestFirst(!sortNewestFirst)} 
              size="small"
              sx={{
                bgcolor: sortNewestFirst ? 'primary.main' : 'action.hover',
                color: sortNewestFirst ? 'white' : 'text.primary',
                '&:hover': { bgcolor: sortNewestFirst ? 'primary.dark' : 'action.selected' },
              }}
            >
              <SortIcon sx={{ transform: sortNewestFirst ? 'none' : 'scaleY(-1)', fontSize: 20 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh">
            <IconButton 
              onClick={loadSubmissions} 
              size="small"
              sx={{ bgcolor: 'action.hover', '&:hover': { bgcolor: 'action.selected' } }}
            >
              <RefreshIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      
      {/* Filter Bar */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1.5, 
        flexWrap: 'wrap',
        mt: 2,
        p: 1.5,
        bgcolor: 'action.hover',
        borderRadius: 1,
      }}>
        {isAdminOrCadre && (
          <TextField
            label={`Search ${singular}`}
            value={studentFilter}
            onChange={(e) => setStudentFilter(e.target.value)}
            size="small"
            placeholder={`${singular}-01...`}
            sx={{ minWidth: 0, flex: '1 1 120px' }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 0.5, fontSize: 18, color: 'action.active' }} />,
            }}
          />
        )}
        
        {/* Date Range */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: '1 1 auto', flexWrap: 'wrap' }}>
          <TextField
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            size="small"
            sx={{ 
              flex: '1 1 110px',
              minWidth: 0,
              '& .MuiInputBase-input': { py: 0.875 },
            }}
          />
          <Typography variant="body2" color="text.secondary">—</Typography>
          <TextField
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            size="small"
            sx={{ 
              flex: '1 1 110px',
              minWidth: 0,
              '& .MuiInputBase-input': { py: 0.875 },
            }}
            inputProps={{
              min: startDate,
            }}
          />
        </Box>
        
        {/* Quick Range Buttons */}
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Chip
            label="Today"
            size="small"
            onClick={() => applyQuickRange(1)}
            variant={activeQuickRange === 'today' ? 'filled' : 'outlined'}
            color={activeQuickRange === 'today' ? 'primary' : 'default'}
            sx={{ cursor: 'pointer' }}
          />
          <Chip
            label="7 Days"
            size="small"
            onClick={() => applyQuickRange(7)}
            variant={activeQuickRange === '7d' ? 'filled' : 'outlined'}
            color={activeQuickRange === '7d' ? 'primary' : 'default'}
            sx={{ cursor: 'pointer' }}
          />
          <Chip
            label="30 Days"
            size="small"
            onClick={() => applyQuickRange(30)}
            variant={activeQuickRange === '30d' ? 'filled' : 'outlined'}
            color={activeQuickRange === '30d' ? 'primary' : 'default'}
            sx={{ cursor: 'pointer' }}
          />
          <Chip
            label="All"
            size="small"
            onClick={() => applyQuickRange(null)}
            variant={activeQuickRange === 'all' ? 'filled' : 'outlined'}
            color={activeQuickRange === 'all' ? 'primary' : 'default'}
            sx={{ cursor: 'pointer' }}
          />
        </Box>
      </Box>

      {/* Empty State */}
      {filteredGroupedEntries.length === 0 && allEntries.length === 0 && filteredNoEntrySubmissions.length === 0 && (
        <EmptyState
          icon={<AssignmentIcon />}
          title={
            groupedEntries.length > 0 && startDate && endDate
              ? `No entries for ${format(parseISO(startDate), 'MMM dd, yyyy')}${startDate !== endDate ? ` - ${format(parseISO(endDate), 'MMM dd, yyyy')}` : ''}`
              : groupedEntries.length > 0
                ? 'No entries match the current filters'
                : 'No route entries yet'
          }
          description={
            groupedEntries.length > 0
              ? 'Try selecting a different date range.'
              : 'Switch to the "Submit Itinerary" tab to create your first submission.'
          }
          sx={{ mt: 2 }}
        />
      )}

      {filteredNoEntrySubmissions.length > 0 && (
        <Stack spacing={1.5} sx={{ mt: 2, mb: 3 }}>
          {filteredNoEntrySubmissions.map((submission) => (
            <Card key={submission.id} variant="outlined">
              <CardContent>
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="body2" fontWeight={700}>
                      {submission.student_number}
                    </Typography>
                    <Chip
                      label={getStatusLabel(submission.status)}
                      color={getStatusColor(submission.status)}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Route Run: {formatDateTime(submission.route_run_datetime)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    No route entries saved yet.
                  </Typography>
                  {isDraftSubmission(submission) && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<EditIcon />}
                      onClick={() => handleReopenSubmission(submission)}
                      sx={{ alignSelf: 'flex-start' }}
                    >
                      Re-open Draft
                    </Button>
                  )}
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}
      
      {/* Grouped Route Entries List */}
      <Stack spacing={3}>
        {filteredGroupedEntries.map((group, groupIndex) => {
          // Get unique submissions for this group
          const uniqueSubmissions = Array.from(
            new Map(group.entries.map(entry => [entry.submission.id, entry.submission])).values()
          );
          
          return (
          <Box key={groupIndex}>
            {/* Date Header */}
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1, 
                mb: 2,
                pb: 1,
                borderBottom: '2px solid',
                borderColor: 'primary.main',
                flexWrap: 'wrap',
              }}
            >
              <ScheduleIcon color="primary" sx={{ flexShrink: 0 }} />
              <Typography variant="subtitle1" color="primary.main" fontWeight={600} noWrap>
                {group.date}
              </Typography>
              <Chip 
                label={`${group.entries.length} ${group.entries.length === 1 ? 'entry' : 'entries'}`} 
                size="small" 
                color="primary"
                variant="outlined"
              />
              <Chip 
                label={`${uniqueSubmissions.length} ${uniqueSubmissions.length === 1 ? 'submission' : 'submissions'}`} 
                size="small" 
                color="secondary"
                variant="outlined"
              />
            </Box>
            
            {/* Route Entries for this date */}
            <Stack spacing={2}>
              {group.entries.map((entry, entryIndex) => {
                const isSelected = entry.id === selectedEntryId;
                return (
                <Card 
                  key={`${entry.id || entryIndex}`}
                  id={`entry-${entry.id}`}
                  sx={{ 
                    cursor: 'pointer',
                    '&:hover': { 
                      boxShadow: isSelected ? 4 : 3,
                      borderColor: 'primary.main',
                    },
                    border: isSelected ? '3px solid' : '1px solid',
                    borderColor: isSelected ? 'primary.main' : 'divider',
                    transition: 'all 0.3s',
                    bgcolor: isSelected ? 'rgba(46, 134, 171, 0.1)' : 'transparent',
                    boxShadow: isSelected ? 2 : 0,
                    transform: isSelected ? 'scale(1.01)' : 'scale(1)',
                  }}
                  onClick={() => handleEntryClickFromList(entry)}
                >
                  <CardContent>
                    <Stack spacing={1}>
                      {/* Route Type and Component - Bold */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="body2" fontWeight={700} sx={{ flex: 1, minWidth: 0 }}>
                          {entry.route_type} - {entry.route_component}
                          {entry.route_component_other && ` (${entry.route_component_other})`}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                          {isSelected && (
                            <Chip 
                              icon={<LocationIcon />}
                              label="Selected" 
                              color="primary" 
                              size="small"
                              sx={{ fontWeight: 600 }}
                            />
                          )}
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {entry.submission.student_number}
                          </Typography>
                          <Chip
                            label={getStatusLabel(entry.submission.status)}
                            color={getStatusColor(entry.submission.status)}
                            size="small"
                            variant="outlined"
                            sx={{ height: 20, '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' } }}
                          />
                          {entry.submission.attachments && entry.submission.attachments.length > 0 && (
                            <Chip 
                              icon={<AttachFileIcon sx={{ fontSize: 14 }} />}
                              label={entry.submission.attachments.length} 
                              size="small"
                              variant="outlined"
                              color="info"
                              sx={{ 
                                height: 20, 
                                '& .MuiChip-label': { px: 0.5, fontSize: '0.7rem' },
                                cursor: 'pointer',
                                '&:hover': { 
                                  backgroundColor: 'info.dark',
                                  transform: 'scale(1.05)',
                                },
                                transition: 'all 0.2s',
                              }}
                              title={`${entry.submission.attachments.length} attachment(s) - Click card to view/download`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewDetails(entry.submission);
                              }}
                            />
                          )}
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMenuOpen(e, entry);
                            }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                      
                      {/* Address - Primary */}
                      <Typography variant="body2" color="text.primary">
                        {entry.location_name_address || 'No address'}
                      </Typography>
                      
                      {/* Arrive/Depart Times - Secondary */}
                      <Typography variant="body2" color="text.secondary">
                        Arrive: {formatDateTime(entry.arrive_datetime)} | Depart: {formatDateTime(entry.depart_datetime)}
                      </Typography>
                      
                      {/* Coordinates - Secondary */}
                      <Typography variant="body2" color="text.secondary">
                        Coordinates: {entry.latitude.toFixed(6)}, {entry.longitude.toFixed(6)}
                      </Typography>
                      
                      {/* Entry Notes - If present */}
                      {entry.notes && (
                        <Box sx={{ mt: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1, borderLeft: '3px solid', borderColor: 'primary.main' }}>
                          <Typography variant="caption" color="text.secondary" fontWeight={600}>
                            Notes:
                          </Typography>
                          <Typography variant="body2" color="text.primary" sx={{ fontStyle: 'italic', mt: 0.5 }}>
                            {entry.notes}
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
                );
              })}
            </Stack>
          </Box>
          );
        })}
      </Stack>
      
      {/* Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssignmentIcon color="primary" />
            Itinerary Details
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedSubmission && (
            <Stack spacing={3}>
              {/* Submission Info */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  SUBMISSION INFO
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Stack spacing={1}>
                  <Typography variant="body2">
                    <strong>{singular}:</strong> {selectedSubmission.student_number}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Status:</strong> {getStatusLabel(selectedSubmission.status)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Submitted:</strong> {formatDateTime(selectedSubmission.date_submitted)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Route Run:</strong> {formatDateTime(selectedSubmission.route_run_datetime)}
                  </Typography>
                </Stack>
              </Box>
              
              {/* Admin Data */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  ADMIN DATA
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Stack spacing={1}>
                  {selectedSubmission.gender && (
                    <Typography variant="body2">
                      <strong>Gender:</strong> {selectedSubmission.gender}
                    </Typography>
                  )}
                  {selectedSubmission.height && (
                    <Typography variant="body2">
                      <strong>Height:</strong> {selectedSubmission.height}
                    </Typography>
                  )}
                  {selectedSubmission.weight && (
                    <Typography variant="body2">
                      <strong>Weight:</strong> {selectedSubmission.weight}
                    </Typography>
                  )}
                  {selectedSubmission.hair_typecolor && (
                    <Typography variant="body2">
                      <strong>Hair:</strong> {selectedSubmission.hair_typecolor}
                    </Typography>
                  )}
                  {selectedSubmission.facial_hair_typecolor && (
                    <Typography variant="body2">
                      <strong>Facial Hair:</strong> {selectedSubmission.facial_hair_typecolor}
                    </Typography>
                  )}
                </Stack>
              </Box>
              
              {/* Vehicle Data */}
              {(selectedSubmission.veh_make || selectedSubmission.veh_model || selectedSubmission.veh_color || selectedSubmission.veh_plate) && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    VEHICLE DATA
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Stack spacing={1}>
                    {selectedSubmission.veh_make && (
                      <Typography variant="body2">
                        <strong>Make:</strong> {selectedSubmission.veh_make}
                      </Typography>
                    )}
                    {selectedSubmission.veh_model && (
                      <Typography variant="body2">
                        <strong>Model:</strong> {selectedSubmission.veh_model}
                      </Typography>
                    )}
                    {selectedSubmission.veh_color && (
                      <Typography variant="body2">
                        <strong>Color:</strong> {selectedSubmission.veh_color}
                      </Typography>
                    )}
                    {selectedSubmission.veh_plate && (
                      <Typography variant="body2">
                        <strong>License Plate:</strong> {selectedSubmission.veh_plate}
                      </Typography>
                    )}
                  </Stack>
                </Box>
              )}
              
              {/* Route Entries */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  ROUTE LOCATIONS ({selectedSubmission.route_entries?.length || 0})
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <List dense>
                  {selectedSubmission.route_entries?.map((entry, index) => (
                    <ListItem key={entry.id || index} sx={{ px: 0, alignItems: 'flex-start', py: 2 }}>
                      <Badge badgeContent={index + 1} color="primary" sx={{ mr: 2, mt: 0.5 }}>
                        <LocationIcon color="action" />
                      </Badge>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                          {entry.route_type} - {entry.route_component}
                        </Typography>
                        <Typography variant="body2" color="text.primary" sx={{ mb: 0.5 }}>
                          {entry.location_name_address || 'No address'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          Arrive: {formatDateTime(entry.arrive_datetime)} | Depart: {formatDateTime(entry.depart_datetime)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Coordinates: {entry.latitude.toFixed(6)}, {entry.longitude.toFixed(6)}
                        </Typography>
                        {entry.notes && (
                          <Box sx={{ mt: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1, borderLeft: '3px solid', borderColor: 'primary.main' }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                              Entry Notes:
                            </Typography>
                            <Typography variant="body2" color="text.primary" sx={{ fontStyle: 'italic', mt: 0.5 }}>
                              {entry.notes}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </ListItem>
                  ))}
                </List>
              </Box>
              
              {/* Notes */}
              {selectedSubmission.notes && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    NOTES
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                    {selectedSubmission.notes}
                  </Typography>
                </Box>
              )}
              
              {/* Attachments */}
              {selectedSubmission.attachments && selectedSubmission.attachments.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    ATTACHMENTS ({selectedSubmission.attachments.length})
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <List dense>
                    {selectedSubmission.attachments.map((attachment) => (
                      <ListItem 
                        key={attachment.id}
                        sx={{ 
                          px: 0, 
                          display: 'flex', 
                          alignItems: 'center',
                          '&:hover': { bgcolor: 'action.hover' },
                          borderRadius: 1,
                          mb: 0.5,
                        }}
                      >
                        <AttachFileIcon sx={{ mr: 1.5, color: 'text.secondary' }} fontSize="small" />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" fontWeight={500}>
                            {attachment.original_filename}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {attachment.file_size ? `${(attachment.file_size / 1024 / 1024).toFixed(2)} MB` : 'Unknown size'} • 
                            {attachment.uploaded_at ? ` Uploaded ${format(parseISO(attachment.uploaded_at), 'MMM dd, yyyy')}` : ' Upload date unknown'}
                          </Typography>
                        </Box>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={async () => {
                            try {
                              await itineraryApi.downloadAttachment(attachment.id, attachment.original_filename);
                            } catch (error) {
                              console.error('Failed to download attachment:', error);
                              const message = error instanceof Error
                                ? error.message
                                : 'Failed to download file. Please try again.';
                              alert(message);
                            }
                          }}
                          title="Download"
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {isDraftSubmission(selectedSubmission) && (
            <Button
              onClick={() => handleReopenSubmission(selectedSubmission)}
              color="warning"
              variant="outlined"
              startIcon={<EditIcon />}
            >
              Re-open Draft
            </Button>
          )}
          <Button onClick={handleCloseDetails} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {isDraftSubmission(selectedMenuEntry?.submission) && (
          <MenuItem onClick={() => handleReopenSubmission(selectedMenuEntry.submission)} sx={{ color: 'warning.main' }}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            Re-open Draft in Submit Tab
          </MenuItem>
        )}
        <MenuItem onClick={() => selectedMenuEntry && handleViewDetails(selectedMenuEntry.submission)}>
          <InfoIcon fontSize="small" sx={{ mr: 1 }} />
          View Details & Download Files
        </MenuItem>
        <Divider />
        {/* <MenuItem onClick={handleAddEntryClick} sx={{ color: 'success.main' }}>
          <LocationIcon fontSize="small" sx={{ mr: 1 }} />
          Add New Entry to This Submission
        </MenuItem> */}
        <MenuItem onClick={handleEditClick}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Edit Entry
        </MenuItem>
        <MenuItem onClick={handleDeleteEntryClick} sx={{ color: 'warning.main' }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete This Entry Only
        </MenuItem>
        <MenuItem onClick={handleDeleteSubmissionClick} sx={{ color: 'error.main' }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete Entire Submission
        </MenuItem>
      </Menu>
      
      {/* Edit Entry Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={!saving ? handleCancelEdit : undefined}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Edit Route Entry
        </DialogTitle>
        <DialogContent dividers>
          {editingEntry && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Route Type</InputLabel>
                <Select
                  value={editFormData.route_type || ''}
                  onChange={(e) => setEditFormData({ ...editFormData, route_type: e.target.value })}
                  label="Route Type"
                >
                  <MenuItem value="Pre-route">Pre-route</MenuItem>
                  <MenuItem value="Post-route">Post-route</MenuItem>
                </Select>
              </FormControl>
              
              <FormControl fullWidth size="small">
                <InputLabel>Route Component</InputLabel>
                <Select
                  value={editFormData.route_component || ''}
                  onChange={(e) => setEditFormData({ ...editFormData, route_component: e.target.value })}
                  label="Route Component"
                >
                  <MenuItem value="SP">SP - Start Point</MenuItem>
                  <MenuItem value="CS">CS - Choke/Switch</MenuItem>
                  <MenuItem value="TP">TP - Turnaround Point</MenuItem>
                  <MenuItem value="VDO">VDO</MenuItem>
                  <MenuItem value="DP">DP - Drop Point</MenuItem>
                  <MenuItem value="Abort">Abort</MenuItem>
                  <MenuItem value="PZ (N/S/E/W)">PZ (N/S/E/W)</MenuItem>
                  <MenuItem value="PC">PC</MenuItem>
                  <MenuItem value="OA">OA</MenuItem>
                  <MenuItem value="LPT P/U">LPT P/U</MenuItem>
                  <MenuItem value="LPT D/O">LPT D/O</MenuItem>
                  <MenuItem value="SVLAR (N/S/E/W)">SVLAR (N/S/E/W)</MenuItem>
                  <MenuItem value="CB (N/S/E/W)">CB (N/S/E/W)</MenuItem>
                  <MenuItem value="CG">CG</MenuItem>
                  <MenuItem value="Drop">Drop</MenuItem>
                  <MenuItem value="Recover">Recover</MenuItem>
                  <MenuItem value="VDO/TWA">VDO/TWA</MenuItem>
                  <MenuItem value="TWA">TWA</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>
              
              {editFormData.route_component === 'Other' && (
                <TextField
                  label="Other Component Description"
                  value={editFormData.route_component_other || ''}
                  onChange={(e) => setEditFormData({ ...editFormData, route_component_other: e.target.value })}
                  size="small"
                  fullWidth
                />
              )}
              
              {/* Map Picker Button for Edit */}
              <Button
                variant="outlined"
                startIcon={<MapIcon />}
                onClick={() => setShowEditMapPicker(!showEditMapPicker)}
                fullWidth
                color={showEditMapPicker ? 'secondary' : 'primary'}
                sx={{ mb: 1 }}
              >
                {showEditMapPicker ? 'Hide Map' : 'Change Location on Map'}
              </Button>
              
              {/* Inline Map Picker for Edit */}
              {showEditMapPicker && (
                <Box sx={{ mb: 2 }}>
                  {/* Map Style Selector */}
                  <Stack direction="row" spacing={0.5} sx={{ mb: 1, justifyContent: 'center' }}>
                    {Object.keys(mapStyles).map((style) => (
                      <Chip
                        key={style}
                        label={style.charAt(0).toUpperCase() + style.slice(1)}
                        size="small"
                        onClick={() => setMapStyle(style)}
                        color={mapStyle === style ? 'primary' : 'default'}
                        variant={mapStyle === style ? 'filled' : 'outlined'}
                        sx={{ textTransform: 'capitalize' }}
                      />
                    ))}
                  </Stack>
                  <Box sx={{ height: 300, borderRadius: 1, overflow: 'hidden', border: '2px solid', borderColor: 'primary.main' }}>
                    <MapGL
                      mapboxAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}
                      initialViewState={{
                        longitude: editFormData.longitude || editingEntry?.longitude || -110.9,
                        latitude: editFormData.latitude || editingEntry?.latitude || 32.2,
                        zoom: 14
                      }}
                      style={{ width: '100%', height: '100%' }}
                      mapStyle={mapStyles[mapStyle]}
                      projection={{name: 'mercator'}}
                      onClick={handleEditMapClick}
                      cursor="crosshair"
                    >
                      <NavigationControl position="top-right" />
                      {(editFormData.latitude || editingEntry?.latitude) && (
                        <Marker
                          longitude={editFormData.longitude || editingEntry?.longitude || 0}
                          latitude={editFormData.latitude || editingEntry?.latitude || 0}
                          color="#2196f3"
                        />
                      )}
                    </MapGL>
                  </Box>
                  <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 0.5, color: 'text.secondary' }}>
                    Click on the map to update location
                  </Typography>
                </Box>
              )}
              
              {isReverseGeocoding && (
                <Alert severity="info" sx={{ mb: 1 }}>
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                  Getting address...
                </Alert>
              )}
              
              <TextField
                label="Location Name/Address"
                value={editFormData.location_name_address || ''}
                onChange={(e) => setEditFormData({ ...editFormData, location_name_address: e.target.value })}
                size="small"
                fullWidth
                multiline
                rows={2}
              />
              
              {/* Show current coordinates */}
              <Typography variant="caption" color="text.secondary">
                Coordinates: {editFormData.latitude?.toFixed(6) || editingEntry?.latitude.toFixed(6)}, {editFormData.longitude?.toFixed(6) || editingEntry?.longitude.toFixed(6)}
              </Typography>
              
              <TextField
                label="Route Run Date"
                type="date"
                value={editFormData.route_run_date || ''}
                onChange={(e) => handleEditRouteRunChange(e.target.value)}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                helperText="Updates the route date for all components; times are unchanged"
              />

              <TextField
                label="Arrive Date/Time"
                type="datetime-local"
                value={editFormData.arrive_datetime ? toDateTimeLocalValue(editFormData.arrive_datetime) : ''}
                onChange={(e) => setEditFormData({ ...editFormData, arrive_datetime: new Date(e.target.value).toISOString() })}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              
              <TextField
                label="Depart Date/Time"
                type="datetime-local"
                value={editFormData.depart_datetime ? toDateTimeLocalValue(editFormData.depart_datetime) : ''}
                onChange={(e) => setEditFormData({ ...editFormData, depart_datetime: new Date(e.target.value).toISOString() })}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              
              <TextField
                label="Notes"
                value={editFormData.notes || ''}
                onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
                size="small"
                fullWidth
                multiline
                rows={3}
                placeholder="Notes specific to this location..."
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelEdit} disabled={saving}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveEdit} 
            color="primary" 
            variant="contained"
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Add Entry Dialog */}
      <Dialog
        open={addEntryDialogOpen}
        onClose={!saving ? handleCancelAddEntry : undefined}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'success.main', color: 'white' }}>
          <LocationIcon />
          Add New Entry to Submission
        </DialogTitle>
        <DialogContent dividers>
          {addingToSubmission && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Alert severity="info" sx={{ mb: 1 }}>
                Adding new entry to <strong>{addingToSubmission.student_number}</strong>'s submission 
                from {formatDateTime(addingToSubmission.route_run_datetime)}
              </Alert>
              
              <FormControl fullWidth size="small">
                <InputLabel>Route Type</InputLabel>
                <Select
                  value={addEntryFormData.route_type || 'Pre-route'}
                  onChange={(e) => setAddEntryFormData({ ...addEntryFormData, route_type: e.target.value })}
                  label="Route Type"
                >
                  <MenuItem value="Pre-route">Pre-route</MenuItem>
                  <MenuItem value="Post-route">Post-route</MenuItem>
                </Select>
              </FormControl>
              
              <FormControl fullWidth size="small">
                <InputLabel>Route Component</InputLabel>
                <Select
                  value={addEntryFormData.route_component || 'CS'}
                  onChange={(e) => setAddEntryFormData({ ...addEntryFormData, route_component: e.target.value })}
                  label="Route Component"
                >
                  <MenuItem value="SP">SP - Start Point</MenuItem>
                  <MenuItem value="CS">CS - Choke/Switch</MenuItem>
                  <MenuItem value="TP">TP - Turnaround Point</MenuItem>
                  <MenuItem value="VDO">VDO</MenuItem>
                  <MenuItem value="DP">DP - Drop Point</MenuItem>
                  <MenuItem value="Abort">Abort</MenuItem>
                  <MenuItem value="PZ (N/S/E/W)">PZ (N/S/E/W)</MenuItem>
                  <MenuItem value="PC">PC</MenuItem>
                  <MenuItem value="OA">OA</MenuItem>
                  <MenuItem value="LPT P/U">LPT P/U</MenuItem>
                  <MenuItem value="LPT D/O">LPT D/O</MenuItem>
                  <MenuItem value="SVLAR (N/S/E/W)">SVLAR (N/S/E/W)</MenuItem>
                  <MenuItem value="CB (N/S/E/W)">CB (N/S/E/W)</MenuItem>
                  <MenuItem value="CG">CG</MenuItem>
                  <MenuItem value="Drop">Drop</MenuItem>
                  <MenuItem value="Recover">Recover</MenuItem>
                  <MenuItem value="VDO/TWA">VDO/TWA</MenuItem>
                  <MenuItem value="TWA">TWA</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>
              
              {addEntryFormData.route_component === 'Other' && (
                <TextField
                  label="Other Component Description"
                  value={addEntryFormData.route_component_other || ''}
                  onChange={(e) => setAddEntryFormData({ ...addEntryFormData, route_component_other: e.target.value })}
                  size="small"
                  fullWidth
                />
              )}
              
              {/* Map Picker Button */}
              <Button
                variant="outlined"
                startIcon={<MapIcon />}
                onClick={() => setShowAddMapPicker(!showAddMapPicker)}
                fullWidth
                color={showAddMapPicker ? 'secondary' : 'primary'}
                sx={{ mb: 1 }}
              >
                {showAddMapPicker ? 'Hide Map' : 'Select Location on Map'}
              </Button>
              
              {/* Inline Map Picker */}
              {showAddMapPicker && (
                <Box sx={{ mb: 2 }}>
                  {/* Map Style Selector */}
                  <Stack direction="row" spacing={0.5} sx={{ mb: 1, justifyContent: 'center' }}>
                    {Object.keys(mapStyles).map((style) => (
                      <Chip
                        key={style}
                        label={style.charAt(0).toUpperCase() + style.slice(1)}
                        size="small"
                        onClick={() => setMapStyle(style)}
                        color={mapStyle === style ? 'primary' : 'default'}
                        variant={mapStyle === style ? 'filled' : 'outlined'}
                        sx={{ textTransform: 'capitalize' }}
                      />
                    ))}
                  </Stack>
                  <Box sx={{ height: 300, borderRadius: 1, overflow: 'hidden', border: '2px solid', borderColor: 'primary.main' }}>
                    <MapGL
                      mapboxAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}
                      initialViewState={{
                        longitude: addEntryFormData.longitude || -110.9,
                        latitude: addEntryFormData.latitude || 32.2,
                        zoom: (addEntryFormData.latitude && addEntryFormData.latitude !== 0) ? 14 : 10
                      }}
                      style={{ width: '100%', height: '100%' }}
                      mapStyle={mapStyles[mapStyle]}
                      projection={{name: 'mercator'}}
                      onClick={handleAddMapClick}
                      cursor="crosshair"
                    >
                      <NavigationControl position="top-right" />
                      {addEntryFormData.latitude && addEntryFormData.latitude !== 0 && (
                        <Marker
                          longitude={addEntryFormData.longitude || 0}
                          latitude={addEntryFormData.latitude || 0}
                          color="#4caf50"
                        />
                      )}
                    </MapGL>
                  </Box>
                  <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 0.5, color: 'text.secondary' }}>
                    Click on the map to set location
                  </Typography>
                </Box>
              )}
              
              {isReverseGeocoding && (
                <Alert severity="info" sx={{ mb: 1 }}>
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                  Getting address...
                </Alert>
              )}
              
              <TextField
                label="Location Name/Address *"
                value={addEntryFormData.location_name_address || ''}
                onChange={(e) => setAddEntryFormData({ ...addEntryFormData, location_name_address: e.target.value })}
                size="small"
                fullWidth
                multiline
                rows={2}
                required
                error={!addEntryFormData.location_name_address}
                helperText={!addEntryFormData.location_name_address ? 'This field is required. Click "Select Location on Map" or enter manually.' : ''}
              />
              
              <Stack direction="row" spacing={2}>
                <TextField
                  label="Latitude"
                  type="number"
                  value={addEntryFormData.latitude || 0}
                  onChange={(e) => setAddEntryFormData({ ...addEntryFormData, latitude: parseFloat(e.target.value) || 0 })}
                  size="small"
                  fullWidth
                  inputProps={{ step: 'any' }}
                />
                <TextField
                  label="Longitude"
                  type="number"
                  value={addEntryFormData.longitude || 0}
                  onChange={(e) => setAddEntryFormData({ ...addEntryFormData, longitude: parseFloat(e.target.value) || 0 })}
                  size="small"
                  fullWidth
                  inputProps={{ step: 'any' }}
                />
              </Stack>
              
              <TextField
                label="Arrive Date/Time"
                type="datetime-local"
                value={addEntryFormData.arrive_datetime || ''}
                onChange={(e) => setAddEntryFormData({ ...addEntryFormData, arrive_datetime: e.target.value })}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              
              <TextField
                label="Depart Date/Time"
                type="datetime-local"
                value={addEntryFormData.depart_datetime || ''}
                onChange={(e) => setAddEntryFormData({ ...addEntryFormData, depart_datetime: e.target.value })}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              
              <TextField
                label="Notes"
                value={addEntryFormData.notes || ''}
                onChange={(e) => setAddEntryFormData({ ...addEntryFormData, notes: e.target.value })}
                size="small"
                fullWidth
                multiline
                rows={3}
                placeholder="Notes specific to this location..."
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelAddEntry} disabled={saving}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveNewEntry} 
            color="success" 
            variant="contained"
            disabled={saving || !addEntryFormData.location_name_address}
          >
            {saving ? 'Adding...' : 'Add Entry'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleting && setDeleteDialogOpen(false)}
      >
        <DialogTitle>
          {deleteType === 'entry' ? 'Delete Route Entry?' : 'Delete Submission?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {deleteType === 'entry' ? (
              <>
                Are you sure you want to delete this route entry?
                <br /><br />
                <strong>Location:</strong> {routeEntryToDelete?.location_name_address || 'Unknown'}
                <br />
                <strong>Type:</strong> {routeEntryToDelete?.route_type} - {routeEntryToDelete?.route_component}
                <br />
                <strong>{singular}:</strong> {routeEntryToDelete?.submission.student_number}
                <br /><br />
                The submission will remain with its other route entries.
                {routeEntryToDelete?.submission.route_entries?.length === 1 && (
                  <>
                    <br /><br />
                    <strong>Note:</strong> This is the only entry in this submission, so the entire submission will be deleted.
                  </>
                )}
              </>
            ) : (
              <>
                Are you sure you want to delete the <strong>entire submission</strong> for{' '}
                <strong>{submissionToDelete?.student_number}</strong> on{' '}
                <strong>{submissionToDelete?.route_run_datetime ? formatDateTime(submissionToDelete.route_run_datetime) : ''}</strong>?
                <br /><br />
                This will delete all <strong>{submissionToDelete?.route_entries?.length || 0} route entries</strong> and any attachments.
                <br /><br />
                This action cannot be undone.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            color="error" 
            variant="contained"
            disabled={deleting}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </Box>
  );
};

export default SubmissionsList;

